import { useEffect, useState } from 'react';
import { Check, Pencil, Plus, X, ChevronDown } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import { apiClient } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { usePermissions } from '@/contexts/PermissionContext';
import { calendarServiceTypeSchema, type CalendarServiceType } from '@easyflow/shared';
import { cn, getApiErrorMessage, getErrorMessage } from '@/lib/utils';
import ServiceCardList from '@/components/calendar/ServiceCardList';

// Il tipo di appuntamento è un TAG: identifica SOLO la durata dello slot.
// Non porta orari né giorni: la disponibilità reale è data da
// calendar_settings (orari/giorni del tenant) ∩ sede ∩ turni/competenze.
type SelectableService = {
  id: string;
  name: string;
  service_type_id: string | null;
  service_type_name?: string | null;
  price?: number | null;
  description?: string | null;
};

// Task #1506: shape minimo del listino prezzi caricato dal backend.
interface PriceListOption {
  id: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
}

const PALETTE = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#84cc16'];
const DEFAULT_COLOR = '#6366f1';

export default function ServiceTypesSettings() {
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('calendar.manage');
  const [serviceTypes, setServiceTypes] = useState<CalendarServiceType[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectableServices, setSelectableServices] = useState<SelectableService[]>([]);
  const [settingsForm, setSettingsForm] = useState<{
    editingTypeId: string;
    name: string;
    duration: number;
    color: string;
    requiresApproval: boolean;
    linkedProductIds: string[];
  }>({ editingTypeId: '', name: '', duration: 30, color: DEFAULT_COLOR, requiresApproval: false, linkedProductIds: [] });
  const [saving, setSaving] = useState(false);
  // Task #1506: listini disponibili + listino selezionato per il preview
  // prezzi nel modal. `''` = listino default del tenant (resolver fallback).
  const [priceLists, setPriceLists] = useState<PriceListOption[]>([]);
  const [selectedPriceListId, setSelectedPriceListId] = useState<string>('');

  const reloadTypes = async () => {
    try {
      const res = await apiClient.get<unknown>('/calendar/types');
      if (!res.success) throw new Error(res.error || 'Impossibile caricare le tipologie');
      setServiceTypes(calendarServiceTypeSchema.array().parse(res.data));
      setLoadError(null);
    } catch {
      setLoadError('Impossibile caricare i tipi di appuntamento.');
    }
  };

  // Task #1506-fix: ricarico solo se ho un listino esplicito; senza listino
  // svuoto la lista (l'utente deve scegliere prima il listino). Passo anche
  // il serviceTypeId così il backend include i pre-abbinati anche se non
  // prezzati nel listino corrente.
  const ensureSelectableServices = async (priceListId: string, serviceTypeId?: string) => {
    if (!priceListId) {
      setSelectableServices([]);
      return;
    }
    try {
      const qs = new URLSearchParams({ priceListId });
      if (serviceTypeId) qs.set('serviceTypeId', serviceTypeId);
      const sr = await apiClient.get<
        {
          id: string;
          name: string;
          service_type_id: string | null;
          service_type_name?: string | null;
          price?: number | null;
          description?: string | null;
        }[]
      >(`/calendar/services/selectable?${qs.toString()}`);
      setSelectableServices(sr?.data || []);
    } catch (_e) { /* silent */ }
  };

  const loadPriceLists = async () => {
    try {
      const r = await apiClient.get<
        { id: string; name: string; is_default: boolean; is_active: boolean }[]
      >('/pricelists');
      const items: PriceListOption[] = (r?.data || []).filter(
        (l) => l.is_active !== false,
      );
      setPriceLists(items);
    } catch (_e) { /* silent: dropdown resta vuoto, fallback su default */ }
  };

  useEffect(() => {
    reloadTypes();
    if (canManage) loadPriceLists();
  }, [canManage]);

  // Quando l'admin cambia listino con l'editor aperto, ricarico servizi
  // collegati e selezionabili applicando il nuovo listino lato backend.
  useEffect(() => {
    if (!settingsForm.editingTypeId) return;
    void ensureSelectableServices(selectedPriceListId, settingsForm.editingTypeId);
  }, [selectedPriceListId, settingsForm.editingTypeId]);

  const closeEditor = () => {
    setSettingsForm({ editingTypeId: '', name: '', duration: 30, color: DEFAULT_COLOR, requiresApproval: false, linkedProductIds: [] });
  };

  const loadTypeIntoForm = async (typeId: string) => {
    if (!canManage) return;
    // Toggle: se sto già modificando questo tipo, chiudo l'editor.
    if (settingsForm.editingTypeId === typeId) {
      closeEditor();
      return;
    }
    const type = serviceTypes.find(t => t.id === typeId);
    if (!type) return;
    let linked: string[] = [];
    try {
      // Task #1506: linked products → solo IDs (i prezzi correnti li mostriamo
      // dalla lista selezionabile, già filtrata per `selectedPriceListId`).
      const lr = await apiClient.get<{ id: string }[]>(`/calendar/types/${typeId}/services`);
      linked = (lr?.data || []).map((p) => p.id);
    } catch {
      toast({ title: 'Errore', description: 'Impossibile caricare i servizi collegati. Nessuna modifica effettuata.', variant: 'destructive' });
      return;
    }
    await ensureSelectableServices(selectedPriceListId, typeId);
    setSettingsForm({
      editingTypeId: typeId,
      name: type.name,
      duration: type.duration || 30,
      color: type.color || DEFAULT_COLOR,
      requiresApproval: !!type.requires_approval,
      linkedProductIds: linked,
    });
  };

  const handleCreateType = async () => {
    if (!canManage) return;
    const name = window.prompt('Nome del nuovo tipo di appuntamento:');
    if (!name || !name.trim()) return;
    try {
      const color = PALETTE[serviceTypes.length % PALETTE.length] ?? DEFAULT_COLOR;
      const res = await apiClient.post<unknown>('/calendar/types', {
        name: name.trim(),
        color,
        duration: 30,
        is_active: true,
      });
      if (!res.success) throw new Error(res.error || 'Impossibile creare il tipo');
      const created = calendarServiceTypeSchema.parse(res.data);
      await reloadTypes();
      if (created?.id) {
        await ensureSelectableServices(selectedPriceListId, created.id);
        // Apre direttamente l'editor inline sul nuovo tipo creato.
        setSettingsForm({
          editingTypeId: created.id,
          name: created.name ?? name.trim(),
          duration: created.duration ?? 30,
          color: created.color ?? color,
          requiresApproval: !!created.requires_approval,
          linkedProductIds: [],
        });
      }
      toast({ title: 'Creato', description: `Tipo «${name.trim()}» creato (durata 30 min)` });
    } catch (error) {
      const msg = getApiErrorMessage(error) || getErrorMessage(error) || 'Impossibile creare il tipo';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!canManage) return;
    try {
      setSaving(true);
      const typeId = settingsForm.editingTypeId;
      if (!settingsForm.name.trim()) {
        toast({ title: 'Errore', description: 'Il nome è obbligatorio', variant: 'destructive' });
        return;
      }
      if (!settingsForm.duration || settingsForm.duration < 5) {
        toast({ title: 'Errore', description: 'La durata minima è 5 minuti', variant: 'destructive' });
        return;
      }
      await apiClient.put(`/calendar/types/${typeId}`, {
        name: settingsForm.name.trim(),
        duration: settingsForm.duration,
        color: settingsForm.color,
        requires_approval: settingsForm.requiresApproval,
      });
      await apiClient.put(`/calendar/types/${typeId}/services`, {
        product_ids: settingsForm.linkedProductIds,
      });
      await reloadTypes();
      toast({ title: 'Salvato', description: `Tipo «${settingsForm.name.trim()}» (${settingsForm.duration} min), ${settingsForm.linkedProductIds.length} servizi collegati` });
      closeEditor();
    } catch (error) {
      const msg = getApiErrorMessage(error) || getErrorMessage(error) || 'Impossibile salvare';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const visibleTypes = serviceTypes.filter(t => t.id && t.is_active);

  // Editor inline: attributi del tag (nome/durata/colore/approvazione) + servizi.
  const renderEditor = (type: CalendarServiceType) => (
    <div className="p-3 sm:p-4 rounded-lg bg-purple-50/60 dark:bg-purple-900/15 border border-purple-200 dark:border-purple-800 space-y-4 min-w-0 overflow-hidden w-full">
      {/* Attributi del tipo (tag di sola durata) */}
      <div className="space-y-3 min-w-0">
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          Il tipo è un'etichetta che imposta solo la durata dello slot. Gli orari
          e i giorni disponibili dipendono dalle impostazioni del calendario,
          dalla sede e dai turni.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end min-w-0">
          <div className="sm:col-span-6 min-w-0">
            <Label className="text-[11px] text-gray-600 dark:text-gray-300">Nome</Label>
            <Input
              type="text"
              value={settingsForm.name}
              onChange={(e) => setSettingsForm(s => ({ ...s, name: e.target.value }))}
              className="h-8 text-xs w-full min-w-0"
              placeholder="Es. Visita, Consulenza…"
            />
          </div>
          <div className="sm:col-span-3 min-w-0">
            <Label className="text-[11px] text-gray-600 dark:text-gray-300">Durata (min)</Label>
            <Input
              type="number"
              min={5}
              step={5}
              value={settingsForm.duration}
              onChange={(e) => setSettingsForm(s => ({ ...s, duration: parseInt(e.target.value) || 0 }))}
              className="h-8 text-xs w-full min-w-0"
            />
          </div>
          <div className="sm:col-span-3 min-w-0">
            <Label className="text-[11px] text-gray-600 dark:text-gray-300">Colore</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={settingsForm.color}
                onChange={(e) => setSettingsForm(s => ({ ...s, color: e.target.value }))}
                className="h-8 w-10 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 cursor-pointer p-0.5"
                aria-label="Colore del tipo"
              />
              <div className="flex items-center gap-1 flex-wrap">
                {PALETTE.map(c => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setSettingsForm(s => ({ ...s, color: c }))}
                    className={cn(
                      'w-4 h-4 rounded-full border',
                      settingsForm.color.toLowerCase() === c.toLowerCase()
                        ? 'ring-2 ring-offset-1 ring-purple-500 border-transparent'
                        : 'border-gray-300 dark:border-gray-600'
                    )}
                    style={{ backgroundColor: c }}
                    aria-label={`Colore ${c}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={settingsForm.requiresApproval}
            onChange={(e) => setSettingsForm(s => ({ ...s, requiresApproval: e.target.checked }))}
            className="rounded border-gray-300 dark:border-gray-600"
          />
          Richiede approvazione manuale prima della conferma
        </label>
      </div>

      {/* Task #1506-fix: selettore listino OBBLIGATORIO. Niente fallback
          "default tenant": l'admin DEVE scegliere esplicitamente il listino.
          Senza scelta → empty state, nessun servizio mostrato. */}
      <div className="space-y-1.5 pt-2 border-t border-purple-200 dark:border-purple-700 min-w-0">
        <Label className="text-xs font-semibold text-purple-700 dark:text-purple-300">
          Listino prezzi <span className="text-red-500">*</span>
        </Label>
        <Select
          value={selectedPriceListId}
          onValueChange={(v) => setSelectedPriceListId(v)}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Seleziona un listino…" />
          </SelectTrigger>
          <SelectContent>
            {priceLists.map((pl) => (
              <SelectItem key={pl.id} value={pl.id}>
                {pl.name}{pl.is_default ? ' · default' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[10px] text-gray-500 dark:text-gray-400">
          Vengono mostrati solo i servizi prezzati nel listino selezionato.
        </p>
      </div>

      {/* Servizi abbinati — card */}
      <div className="space-y-2 pt-2 border-t border-purple-200 dark:border-purple-700 min-w-0">
        <Label className="text-xs font-semibold text-purple-700 dark:text-purple-300">
          Servizi abbinati ({settingsForm.linkedProductIds.length})
        </Label>
        <div className="max-h-[260px] overflow-y-auto overflow-x-hidden border border-gray-200 dark:border-gray-700 rounded p-2 bg-white/60 dark:bg-gray-800/60 min-w-0">
          {!selectedPriceListId ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 py-6 text-center">
              Seleziona un listino prezzi sopra per vedere i servizi disponibili.
            </p>
          ) : (
            <ServiceCardList
              mode="multi"
              emptyText="Nessun servizio prezzato in questo listino."
              services={selectableServices.map(svc => ({
                id: svc.id,
                name: svc.name,
                price: svc.price ?? null,
                description: svc.description ?? null,
                linkedToOtherTypeName:
                  svc.service_type_id && svc.service_type_id !== type.id
                    ? svc.service_type_name ?? null
                    : null,
              }))}
              selectedIds={settingsForm.linkedProductIds}
              onToggle={(id) => setSettingsForm(s => ({
                ...s,
                linkedProductIds: s.linkedProductIds.includes(id)
                  ? s.linkedProductIds.filter(x => x !== id)
                  : [...s.linkedProductIds, id],
              }))}
            />
          )}
        </div>
      </div>

      {/* Azioni: Annulla + Salva */}
      <div className="flex items-center gap-2 pt-2">
        <Button
          variant="outline"
          className="flex-1 h-9 text-sm"
          onClick={closeEditor}
          disabled={saving}
        >
          Annulla
        </Button>
        <Button
          className="flex-1 bg-purple-600 hover:bg-purple-700 h-9 text-sm"
          onClick={handleSave}
          disabled={saving}
        >
          <Check className="w-4 h-4 mr-1" />
          {saving ? 'Salvataggio…' : 'Salva configurazione'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-3 min-w-0 w-full">
      {!canManage && <p className="text-sm text-muted-foreground">Sola lettura: la modifica richiede il permesso di gestione Calendario.</p>}
      {loadError && <p role="alert" className="text-sm text-destructive">{loadError}</p>}
      {/* Header lista con titolo + shortcut "+ Nuovo tipo" come icona */}
      <div className="flex items-center justify-between gap-2 min-w-0">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
          Tipi di appuntamento
          {visibleTypes.length > 0 && (
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({visibleTypes.length})</span>
          )}
        </h4>
        {canManage && <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/30 flex-shrink-0"
          onClick={handleCreateType}
          title="Nuovo tipo di appuntamento"
          aria-label="Nuovo tipo di appuntamento"
        >
          <Plus className="w-4 h-4" />
        </Button>}
      </div>

      {visibleTypes.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
          Nessun tipo configurato.{canManage && <> Usa il pulsante <Plus className="inline w-3 h-3 mx-1" /> per crearne uno.</>}
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg w-full min-w-0 overflow-hidden">
          {/* Intestazione "tabella" — visibile da sm+ */}
          <div className="hidden sm:grid grid-cols-[1fr_90px_70px_50px] gap-2 items-center px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-[11px] font-medium text-gray-600 dark:text-gray-400">
            <div>Tipo</div>
            <div className="text-center">Durata</div>
            <div className="text-center">Servizi</div>
            <div></div>
          </div>

          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {visibleTypes.map((type) => {
              const isOpen = settingsForm.editingTypeId === type.id;
              return (
                <li key={type.id} className="bg-white dark:bg-gray-900">
                  {/* Riga principale */}
                  <button
                    type="button"
                    disabled={!canManage}
                    onClick={() => loadTypeIntoForm(type.id)}
                    className={cn(
                      'w-full grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_90px_70px_50px] gap-2 items-center px-3 py-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors min-w-0',
                      isOpen && 'bg-purple-50 dark:bg-purple-900/20',
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: type.color ?? DEFAULT_COLOR }} />
                      <span className="text-gray-900 dark:text-gray-100 truncate font-medium">{type.name}</span>
                    </div>
                    {/* Su mobile mostro solo l'icona toggle a destra; sm+ mostra le colonne */}
                    <div className="hidden sm:block text-center text-gray-700 dark:text-gray-300 tabular-nums">{type.duration} min</div>
                    <div className="hidden sm:block text-center text-gray-700 dark:text-gray-300">{type.linked_services_count ?? 0}</div>
                    <div className="flex items-center justify-end gap-1 flex-shrink-0">
                      {/* Riepilogo compatto su mobile (durata · servizi) */}
                      <span className="sm:hidden text-[10px] text-gray-500 dark:text-gray-400 mr-1">
                        {type.duration}min · {type.linked_services_count ?? 0}s
                      </span>
                      {canManage && <span
                        className={cn(
                          'inline-flex items-center justify-center w-7 h-7 rounded text-gray-500 dark:text-gray-400',
                          isOpen ? 'bg-purple-200 dark:bg-purple-800/40 text-purple-700 dark:text-purple-200' : 'hover:text-purple-600 dark:hover:text-purple-400'
                        )}
                        title={isOpen ? 'Chiudi modifica' : 'Modifica'}
                        aria-label={isOpen ? 'Chiudi modifica' : 'Modifica'}
                      >
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                      </span>}
                    </div>
                  </button>

                  {/* Editor inline espandibile */}
                  {isOpen && canManage && (
                    <div className="px-2 sm:px-3 pb-3 pt-1 border-t border-purple-100 dark:border-purple-900/30 bg-gray-50/60 dark:bg-gray-900/40">
                      <div className="flex items-center justify-end mb-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                          onClick={closeEditor}
                          title="Chiudi"
                          aria-label="Chiudi"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      {renderEditor(type)}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
